import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

import type { Watchlist } from "@/services/api/types";
import { apiClient } from "@/services/api/client";

interface MembershipRow {
  watchlist: Watchlist;
  checked: boolean;
  itemId: number | null;
}

interface AddIntoWatchlistModalProps {
  open: boolean;
  symbol: string;
  displayName?: string;
  watchlists: Watchlist[];
  maxListsPerUser: number;
  onClose: () => void;
  onAfterChange?: () => Promise<void> | void;
}

export function AddIntoWatchlistModal({
  open,
  symbol,
  displayName,
  watchlists,
  maxListsPerUser,
  onClose,
  onAfterChange,
}: AddIntoWatchlistModalProps) {
  const [rows, setRows] = useState<MembershipRow[]>([]);
  const [initialState, setInitialState] = useState<Record<number, { checked: boolean; itemId: number | null }>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createName, setCreateName] = useState("");

  const maxReached = watchlists.length >= maxListsPerUser;

  useEffect(() => {
    if (!open) {
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    void Promise.all(
      watchlists.map(async (watchlist) => {
        const payload = await apiClient.listWatchlistItems(watchlist.id, { limit: 5000 });
        const matched = (payload.items || []).find((item) => item.symbol === symbol) || null;
        return {
          watchlist,
          checked: Boolean(matched),
          itemId: matched?.id ?? null,
        };
      }),
    )
      .then((list) => {
        if (!active) {
          return;
        }
        setRows(list);
        const nextInitial: Record<number, { checked: boolean; itemId: number | null }> = {};
        list.forEach((item) => {
          nextInitial[item.watchlist.id] = { checked: item.checked, itemId: item.itemId };
        });
        setInitialState(nextInitial);
      })
      .catch((err: unknown) => {
        if (!active) {
          return;
        }
        setError(err instanceof Error ? err.message : "加载自选股状态失败");
      })
      .finally(() => {
        if (!active) {
          return;
        }
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, symbol, watchlists]);

  const changedCount = useMemo(() => {
    return rows.reduce((acc, row) => {
      const initial = initialState[row.watchlist.id];
      if (!initial) {
        return acc + (row.checked ? 1 : 0);
      }
      return acc + (initial.checked === row.checked ? 0 : 1);
    }, 0);
  }, [rows, initialState]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      for (const row of rows) {
        const initial = initialState[row.watchlist.id];
        const wasChecked = Boolean(initial?.checked);
        if (row.checked === wasChecked) {
          continue;
        }
        if (row.checked) {
          await apiClient.addWatchlistItem(row.watchlist.id, { symbol });
          continue;
        }
        const targetItemId = row.itemId || initial?.itemId;
        if (targetItemId) {
          await apiClient.removeWatchlistItem(row.watchlist.id, targetItemId);
        }
      }
      if (onAfterChange) {
        await onAfterChange();
      }
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "更新自选股失败");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateAndAdd() {
    const name = createName.trim();
    if (!name) {
      return;
    }
    if (maxReached) {
      setError(`最多可创建 ${maxListsPerUser} 个自选股`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await apiClient.createWatchlist({ name, sort_by: "pct_chg", sort_dir: "desc" });
      await apiClient.addWatchlistItem(created.id, { symbol });
      setCreateName("");
      if (onAfterChange) {
        await onAfterChange();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "创建自选股失败");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-[560px] rounded-lg border border-[var(--border)] bg-[#11161e] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-100">加入自选股</div>
            <div className="mt-1 text-xs text-[var(--muted)]">
              {displayName || symbol} · {symbol}
            </div>
          </div>
          <button
            className="inline-flex size-8 items-center justify-center rounded border border-[var(--border)] text-slate-300 hover:bg-white/5"
            onClick={onClose}
            aria-label="关闭"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <input
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              placeholder="新建自选股名称"
              className="h-8 min-w-0 flex-1 rounded border border-[var(--border)] bg-[#0c1016] px-2 text-sm text-slate-200"
              maxLength={60}
            />
            <button
              className="h-8 rounded border border-[var(--border)] bg-[#182132] px-3 text-xs text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void handleCreateAndAdd()}
              disabled={saving || !createName.trim() || maxReached}
            >
              新建并加入
            </button>
          </div>
          <div className="text-[11px] text-[var(--muted)]">
            已有 {watchlists.length} / {maxListsPerUser} 个自选股
          </div>

          <div className="terminal-scrollbar max-h-[340px] overflow-y-auto rounded border border-[var(--border)] bg-[#0d1117]">
            {loading ? (
              <div className="px-3 py-8 text-center text-sm text-[var(--muted)]">加载中...</div>
            ) : rows.length ? (
              rows.map((row) => (
                <label
                  key={row.watchlist.id}
                  className="flex cursor-pointer items-center justify-between gap-2 border-b border-[var(--border)] px-3 py-2.5 text-sm last:border-b-0 hover:bg-white/5"
                >
                  <span className="truncate text-slate-100">{row.watchlist.name}</span>
                  <input
                    type="checkbox"
                    className="size-4 accent-emerald-500"
                    checked={row.checked}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setRows((prev) =>
                        prev.map((item) =>
                          item.watchlist.id === row.watchlist.id
                            ? {
                                ...item,
                                checked,
                              }
                            : item,
                        ),
                      );
                    }}
                  />
                </label>
              ))
            ) : (
              <div className="px-3 py-8 text-center text-sm text-[var(--muted)]">暂无自选股</div>
            )}
          </div>

          {error ? <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div> : null}
        </div>

        <div className="flex items-center justify-between border-t border-[var(--border)] px-4 py-3">
          <div className="text-xs text-[var(--muted)]">{changedCount > 0 ? `待提交变更 ${changedCount} 项` : "无待提交变更"}</div>
          <div className="flex items-center gap-2">
            <button
              className="h-8 rounded border border-[var(--border)] bg-transparent px-3 text-xs text-slate-200 hover:bg-white/5"
              onClick={onClose}
              disabled={saving}
            >
              取消
            </button>
            <button
              className="h-8 rounded border border-emerald-500/40 bg-emerald-500/20 px-3 text-xs text-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void handleSave()}
              disabled={saving || loading || changedCount <= 0}
            >
              {saving ? "保存中..." : "保存变更"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
